import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import JourneyStepProp from '../../propTypes/journeyStepProp';
import translate from '../../../../commonComponents/translate';
import {
  compose,
  downloadFile,
  JOURNEY_STEP_STATUS,
} from '../../../../services/utilityFunctions';
import Notifications from '../../../../services/notifications';
import withCSRF from '../../../../commonComponents/withCSRF';
import InfoSection from './components/InfoSection';
import FormInput from '../../../../commonComponents/forms/FormInput';
import DocumentCard from '../../../../commonComponents/cards/DocumentCard';
import Loader from '../../../../services/loader';
import SkipStepButton from './components/SkipStepButton';

function arraysAreEqual(arr1, arr2) {
  if (arr1.length !== arr2.length) {
    return false;
  }

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < arr1.length; i++) {
    const obj1 = arr1[i];
    const obj2 = arr2[i];

    // Compare properties and values of objects
    for (const key in obj1) {
      // eslint-disable-next-line no-prototype-builtins
      if (obj1.hasOwnProperty(key) && obj2.hasOwnProperty(key)) {
        if (obj1[key] !== obj2[key]) {
          return false;
        }
      }
    }
  }

  return true;
}

function JourneyUploadDocument(props) {
  const { t, _csrf, step, onRefreshJourney, onSkipStep } = props;
  const {
    documents,
    _id,
    maxDays,
    title,
    description,
    taskStatus: stepTaskStatus,
    userDocuments = [],
  } = step;
  const taskStatus =
    stepTaskStatus && stepTaskStatus === 'approved' ? 'done' : stepTaskStatus;
  const [documentsState, setDocumentState] = useState(userDocuments || []);
  const [isSameDocument, setIsSameDocument] = useState(
    arraysAreEqual(userDocuments, documentsState),
  );
  const [isLoading, setIsLoading] = useState(false);
  const loader = useRef(new Loader('#document'));

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  const showUploadProgress = () => {
    setIsLoading(true);
  };

  const handleUploadLoading = () => {
    setIsLoading(false);
  };

  const handleChange = useCallback(
    value => {
      setDocumentState(value.filter(itemA => !itemA._id));
    },
    [setDocumentState],
  );

  const handleRemove = useCallback(
    file => {
      setDocumentState(prevState => {
        const newDocs = prevState.filter(f => f.name !== file.name);
        $(`#${file.size}`).remove();
        return newDocs;
      });
    },
    [setDocumentState],
  );

  const onSubmitDoc = () => {
    setIsLoading(true);
    axios
      .put(
        `/api/journey/step/${_id}/document/signed`,
        {
          documents: documentsState,
        },
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      )
      .then(() => {
        setIsLoading(false);
        Notifications.showNotificationSuccess(
          t('success'),
          t('journey.step.doc.upload.success'),
        );
        onRefreshJourney();
      })
      .catch(err => {
        Notifications.showErrorFromRequest(
          err,
          'journey.step.doc.upload.error',
        );
        console.log(err);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    setIsSameDocument(arraysAreEqual(userDocuments, documentsState));
  }, [documentsState]);

  return (
    <div id="uploadDocumentContainer" className="row">
      <div className="col-lg-5 col-12">
        <InfoSection
          sectionTitle={t('journey.section.document.title')}
          title={title}
          description={description}
          maxDays={maxDays}
          status={taskStatus}
          customContent={documents.map(file => (
            <DocumentCard
              file={file}
              onClickDownload={() => downloadFile(file)}
            />
          ))}
        />
      </div>
      <div className="col-lg-7 col-12">
        <div className="m-portlet m-portlet-rounded">
          <div className="m-portlet__body uploadDocument-container">
            <div className="header">
              <h5 className="title">{t('upload.document')}</h5>
              {onSkipStep && <SkipStepButton onClick={onSkipStep} />}
            </div>
            <div
              className={`inputWrapper ${
                documentsState?.length > 0 ? 'inputFilled' : ''
              }`}
            >
              <FormInput
                id="document"
                name="document"
                value={JSON.stringify(documentsState || [])}
                onChange={handleChange}
                placeholder={t('journey.section.document.browseOrDrag')}
                containerClassName="docContainer"
                type="dropZone"
                inputProps={{
                  maxFiles: 1,
                  isImage: false,
                  isCustomTemplate: true,
                  onRemoveFile:
                    taskStatus && taskStatus === JOURNEY_STEP_STATUS.DONE
                      ? null
                      : handleRemove,
                  onUploadProgress: progress => showUploadProgress(progress),
                  onCompleted: finished => handleUploadLoading(finished),
                }}
              />
            </div>
            {documentsState.length > 0 &&
            (!taskStatus || taskStatus !== JOURNEY_STEP_STATUS.DONE) ? (
              <div className="journey-upload-doc">
                <button
                  type="button"
                  onClick={onSubmitDoc}
                  className={`btn btn-dynamic ${
                    isLoading ? ' m-loader m-loader--right m-loader--light' : ''
                  }`}
                  disabled={isLoading || isSameDocument}
                >
                  {t('journey.content.document.upload')}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

JourneyUploadDocument.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  step: PropTypes.shape(JourneyStepProp.propType).isRequired,
  onRefreshJourney: PropTypes.func.isRequired,
  onSkipStep: PropTypes.func.isRequired,
};

JourneyUploadDocument.defaultProps = {};

export default compose(translate, withCSRF)(JourneyUploadDocument);
